import { useMemo } from 'react';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { useStoreActions } from '@discover-data/stores/restaurant/hooks';
import getTagOptions from '@discover-data/stores/hooks/tagIds/getOptions';
import useDiscountOnly from '@discover-data/stores/hooks/discountOnly/useDiscountOnly';
import useEndTime from '@discover-data/stores/hooks/times/end/useEndTime';
import useHasLastTable from '@discover-data/stores/hooks/hasLastTable/useHasLastTable';
import usePage from '@discover-data/stores/hooks/page/usePage';
import useResetFilters from '@discover-data/stores/hooks/useResetFilters';
import useStartTime from '@discover-data/stores/hooks/times/start/useStartTime';
import useTagIds from '@discover-data/stores/hooks/tagIds/useTagIds';
import Button from '@discover-ui/button';

export type EmptyMessageResetButtonProps = {
  tags: RestaurantTagType[];
};

export default function EmptyMessageResetButton({ tags }: EmptyMessageResetButtonProps) {
  const [tagIds] = useTagIds(tags);
  const [hasLastTable] = useHasLastTable();
  const [startTime] = useStartTime();
  const [endTime] = useEndTime();
  const { resetPage } = usePage();
  const [discountOnly] = useDiscountOnly();
  const setStoreDiscountOnly = useStoreActions((actions) => actions.discountOnly.set);

  // using `useResetFilters` instead of `useTagIds` and `useHasLastTable` seperately because we need to set both at the same time and the `useQueryState` hook can't handle simultaneous set calls
  const { optionKeys } = getTagOptions(tags);
  const routerKeys = useMemo(
    () => [
      'discount-only',
      'has-last-table',
      ...optionKeys, // tags
      'start-time',
      'end-time',
    ],
    [optionKeys]
  );
  const resetFilters = useResetFilters(routerKeys);

  if (
    !discountOnly &&
    tagIds.length === 0 &&
    !hasLastTable &&
    startTime === null &&
    endTime === null
  ) {
    return null;
  }

  return (
    <Button
      kind="cta"
      onClick={() => {
        setStoreDiscountOnly(false); // explicitly set discountOnly to false since the router updates get ignored if the value is unset
        resetFilters();
        resetPage();
      }}>
      Reset all filters
    </Button>
  );
}
